import React, { useEffect } from 'react';
import { 
  FiUser, FiFileText, FiMapPin, FiActivity, FiAlertCircle, 
  FiMessageSquare, FiDollarSign, FiHeadphones, FiLayers
} from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import CollapsibleSection from './CollapsibleSection';
import { Timestamp } from './ui/timestamp';
import { useCase } from '../contexts/CaseContext';

interface CaseDetailPanelProps {
  caseData?: any; // Make optional since we can get data from context
  onToggleOlivia?: () => void; // Make optional since we can get from context
}

const CaseDetailPanel: React.FC<CaseDetailPanelProps> = ({ caseData: propCaseData, onToggleOlivia: propToggleOlivia }) => {
  // Use context with fallback to props
  const { caseData: contextCaseData, toggleOliviaSidebar } = useCase();
  
  // Use either the prop data or context data, prop takes precedence
  const caseData = propCaseData || contextCaseData;
  const onToggleOlivia = propToggleOlivia || toggleOliviaSidebar;
  
  if (!caseData) {
    return (
      <div className="flex items-center justify-center h-full p-6 text-secondary/70">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <IconWrapper icon={FiFileText} size={32} className="text-secondary/40" />
          </div>
          <p className="text-center text-lg font-medium mb-2">No case selected</p>
          <p className="text-center text-sm">Select a case from the list to view its details</p>
        </div>
      </div>
    );
  }
  
  // Helper function to get badge color based on risk level
  const getRiskBadgeColor = (level: string) => {
    if (level.toLowerCase().includes('high')) return 'danger';
    if (level.toLowerCase().includes('medium')) return 'warning';
    return 'success';
  };
  
  // Helper function to get status icon and color
  const getStatusInfo = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return { icon: FiFileText, color: 'text-gray-500' };
      case 'open':
        return { icon: FiAlertCircle, color: 'text-amber-500' };
      case 'in progress':
        return { icon: FiActivity, color: 'text-blue-500' };
      case 'resolved':
        return { icon: FiActivity, color: 'text-green-500' };
      default:
        return { icon: FiActivity, color: 'text-secondary' };
    }
  };
  
  const statusInfo = getStatusInfo(caseData.status);
  
  return (
    <div className="h-full overflow-y-auto bg-white p-6 rounded-lg">
      {/* Header with talk to Olivia button */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center mb-1">
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
              caseData.status.toLowerCase() === 'draft' ? 'bg-gray-500' :
              caseData.status.toLowerCase() === 'open' ? 'bg-amber-500' :
              caseData.status.toLowerCase() === 'in progress' ? 'bg-blue-500' :
              'bg-green-500'}`}
            ></span>
            <span className="text-sm font-medium text-secondary/70">{caseData.status}</span>
            <span className="mx-2 text-secondary/40">•</span>
            <span className="text-sm text-secondary/70">{caseData.id}</span>
            <span className="mx-2 text-secondary/40">•</span>
            <span className="text-sm text-secondary/70">Source: {caseData.source}</span>
          </div>
          <h2 className="text-xl font-bold text-secondary">{caseData.title}</h2>
        </div>
        
        <button
          onClick={onToggleOlivia}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          aria-label="Talk to Olivia about this case"
        >
          <IconWrapper icon={FiHeadphones} size={18} />
          <span>Talk to Olivia</span>
        </button>
      </div>
      
      {/* Progressive disclosure sections */}
      <CollapsibleSection 
        title="Customer Details" 
        icon={FiUser}
        defaultOpen={true}
        summary={caseData.customer}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <IconWrapper icon={FiUser} className="mr-2 text-primary" size={18} />
            <div>
              <div className="text-xs text-secondary/70">Customer Name</div>
              <div className="font-medium">{caseData.customer}</div>
            </div>
          </div>
          
          {caseData.customerDetails && (
            <>
              <div className="flex items-center">
                <IconWrapper icon={FiMapPin} className="mr-2 text-primary" size={18} />
                <div>
                  <div className="text-xs text-secondary/70">Branch</div>
                  <div className="font-medium">{caseData.customerDetails.branch}</div>
                </div>
              </div>
              <div className="flex items-center">
                <IconWrapper icon={FiFileText} className="mr-2 text-primary" size={18} />
                <div>
                  <div className="text-xs text-secondary/70">Account Type</div>
                  <div className="font-medium">{caseData.customerDetails.accountType}</div>
                </div>
              </div>
              <div className="flex items-center">
                <IconWrapper icon={FiDollarSign} className="mr-2 text-primary" size={18} />
                <div>
                  <div className="text-xs text-secondary/70">Account Number</div>
                  <div className="font-medium">{caseData.customerDetails.accountNumber}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>
      
      <CollapsibleSection 
        title="Case Details" 
        icon={FiFileText}
        defaultOpen={true}
        summary={`${caseData.category} - ${caseData.subcategory}`}
      >
        <div className="mb-4">
          <div className="text-xs text-secondary/70 mb-1">Description</div>
          <div className="p-3 bg-gray-50 rounded-lg text-sm leading-relaxed">{caseData.description}</div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-secondary/70 mb-1">Category</div>
            <div className="text-sm font-medium">{caseData.category}</div>
          </div>
          <div>
            <div className="text-xs text-secondary/70 mb-1">Subcategory</div>
            <div className="text-sm font-medium">{caseData.subcategory}</div>
          </div>
          <div>
            <div className="text-xs text-secondary/70 mb-1">Priority</div>
            <div className="text-sm font-medium flex items-center">
              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                ${caseData.priority === 'High' ? 'bg-red-100 text-red-800' : 
                caseData.priority === 'Medium' ? 'bg-amber-100 text-amber-800' : 
                'bg-green-100 text-green-800'}`}
              >
                {caseData.priority}
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs text-secondary/70 mb-1">Date Received</div>
            <div className="text-sm font-medium"><Timestamp value={caseData.dateReceived} format="date-only" /></div>
          </div>
          <div>
            <div className="text-xs text-secondary/70 mb-1">Target Resolution</div>
            <div className="text-sm font-medium"><Timestamp value={caseData.targetResolutionDate} format="date-only" /></div>
          </div>
        </div>
      </CollapsibleSection>
      
      {caseData.aiInsights && (
        <CollapsibleSection 
          title="AI Insights" 
          icon={FiActivity}
          badge={caseData.aiInsights.riskLevel.includes('High') ? 'High Risk' : 
                caseData.aiInsights.riskLevel.includes('Medium') ? 'Medium Risk' : 'Low Risk'}
          badgeColor={getRiskBadgeColor(caseData.aiInsights.riskLevel)}
          summary="Root cause and suggested actions"
        >
          <div className="mb-4">
            <div className="text-xs text-secondary/70 mb-1">Root Cause Analysis</div>
            <div className="p-3 bg-gray-50 rounded-lg text-sm">{caseData.aiInsights.rootCause}</div>
          </div>
          
          <div className="mb-4">
            <div className="text-xs text-secondary/70 mb-1">Suggested Actions</div>
            <ul className="list-disc list-inside p-3 bg-gray-50 rounded-lg text-sm space-y-1">
              {caseData.aiInsights.suggestedActions.map((action: string, index: number) => (
                <li key={index}>{action}</li>
              ))}
            </ul>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-secondary/70 mb-1">Similar Issues</div>
              <div className="text-sm font-medium">{caseData.aiInsights.similarIssues} in last 30 days</div>
            </div>
            <div>
              <div className="text-xs text-secondary/70 mb-1">Pattern</div>
              <div className="text-sm">{caseData.aiInsights.pattern}</div>
            </div>
            <div>
              <div className="text-xs text-secondary/70 mb-1">Risk Level</div>
              <div className={`text-sm font-medium ${
                caseData.aiInsights.riskLevel.includes('High') ? 'text-red-600' :
                caseData.aiInsights.riskLevel.includes('Medium') ? 'text-amber-600' :
                'text-green-600'
              }`}>
                {caseData.aiInsights.riskLevel}
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}
      
      {caseData.interactions && caseData.interactions.length > 0 && (
        <CollapsibleSection 
          title="Interaction History" 
          icon={FiMessageSquare}
          badge={caseData.interactions.length}
          summary={`Latest: ${caseData.interactions[caseData.interactions.length-1]?.notes?.substring(0, 50)}...`}
        >
          <div className="space-y-3">
            {caseData.interactions.map((interaction: any, index: number) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg border-l-4 border-primary">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-sm font-medium">{interaction.type}</span>
                    {interaction.agent && (
                      <span className="text-xs text-secondary/70 ml-2">by {interaction.agent}</span>
                    )}
                  </div>
                  <span className="text-xs text-secondary/70">
                    <Timestamp value={interaction.date} format="absolute" />
                  </span>
                </div>
                <div className="text-sm mt-1">{interaction.notes}</div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}
      
      {caseData.ncdClassification && (
        <CollapsibleSection 
          title="Framework Classification" 
          icon={FiLayers}
          summary={caseData.ncdClassification.principle}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-secondary/70 mb-1">Principle</div>
              <div className="text-sm font-medium">{caseData.ncdClassification.principle}</div>
            </div>
            
            <div>
              <div className="text-xs text-secondary/70 mb-1">Cross-cutting Rules</div>
              <div>
                {caseData.ncdClassification.crossCuttingRules.map((rule: string, index: number) => (
                  <span 
                    key={index} 
                    className="inline-flex items-center justify-center bg-gray-100 px-2 py-0.5 rounded-full text-xs mr-1 mb-1"
                  >
                    {rule}
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <div className="text-xs text-secondary/70 mb-1">Outcome</div>
              <div className="text-sm font-medium">{caseData.ncdClassification.outcome}</div>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default CaseDetailPanel;